import axios from "axios";

const BASE = import.meta.env.VITE_API_BASE_URL;

const axiosInstance = axios.create({
  baseURL: BASE,
  headers: { "Content-Type": "application/json" },
});

// Attach JWT to every request
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Token refresh on 401 ─────────────────────────────────────
// Multiple concurrent requests that all get a 401 are queued;
// only one refresh call is made. All queued requests retry with
// the new token. If refresh itself fails, redirect to signin.

let isRefreshing = false;
let waitQueue = [];

function drainQueue(error, token) {
  waitQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  waitQueue = [];
}

async function refreshToken() {
  const current = localStorage.getItem("token");
  const res = await axios.post(
    `${BASE}/User/refresh`,
    {},
    { headers: { Authorization: `Bearer ${current}` } }
  );
  const newToken = res.data.token;
  localStorage.setItem("token", newToken);
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${newToken}`;
  return newToken;
}

function redirectToSignin() {
  localStorage.removeItem("token");
  window.location.href = "/heycallcenter/signin";
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    // Don't retry the refresh call itself
    if (original.url?.includes("/User/refresh")) {
      redirectToSignin();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        waitQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const token = await refreshToken();
      drainQueue(null, token);
      original.headers.Authorization = `Bearer ${token}`;
      return axiosInstance(original);
    } catch (refreshError) {
      drainQueue(refreshError, null);
      redirectToSignin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance;
